<template>
  <header class="sticky top-0 lg:left-auto h-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 z-30 transition-all duration-300">
    <div class="h-full flex items-center justify-between px-4 lg:px-6">
      <!-- Left Section -->
      <div class="flex items-center gap-4">
        <!-- Mobile Menu Button -->
        <button
          class="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="$emit('openMobileSidebar')"
        >
          <Menu class="w-5 h-5" />
        </button>

        <!-- Desktop Sidebar Toggle -->
        <button
          class="hidden lg:flex p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          @click="$emit('toggleSidebar')"
        >
          <PanelLeft class="w-5 h-5" />
        </button>

        <!-- Breadcrumbs -->
        <nav class="hidden md:flex items-center gap-2 text-sm">
          <template v-for="(crumb, index) in breadcrumbs" :key="index">
            <router-link
              v-if="crumb.to"
              :to="crumb.to"
              class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {{ crumb.label }}
            </router-link>
            <span v-else class="text-gray-900 dark:text-white font-medium">
              {{ crumb.label }}
            </span>
            <ChevronRight v-if="index < breadcrumbs.length - 1" class="w-4 h-4 text-gray-400" />
          </template>
        </nav>
      </div>

      <!-- Right Section -->
      <div class="flex items-center gap-2 lg:gap-4">
        <!-- Dark Mode Toggle -->
        <button
          class="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          @click="toggleDarkMode"
        >
          <Sun v-if="isDark" class="w-5 h-5" />
          <Moon v-else class="w-5 h-5" />
        </button>

        <!-- Notifications -->
        <div class="relative hidden">
          <button
            class="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 relative"
            @click="showNotifications = !showNotifications"
          >
            <Bell class="w-5 h-5" />
            <span class="absolute top-1 right-1 w-2 h-2 bg-pink-400 rounded-full" />
          </button>

          <!-- Notifications Dropdown -->
          <div
            v-if="showNotifications"
            class="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2"
          >
            <div class="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <h3 class="font-semibold text-gray-900 dark:text-white">Notifications</h3>
            </div>
            <div class="max-h-64 overflow-y-auto">
              <div
                v-for="notification in notifications"
                :key="notification.id"
                class="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <p class="text-sm text-gray-900 dark:text-white">{{ notification.message }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ notification.time }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- User Menu -->
        <div class="relative">
          <button
            class="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            @click="showUserMenu = !showUserMenu"
          >
            <div class="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center overflow-hidden">
              <img
                v-if="authStore.user?.avatar_url"
                :src="authStore.user.avatar_url"
                :alt="displayName"
                class="w-full h-full object-cover"
                loading="lazy"
                referrerpolicy="no-referrer"
                @error="$event.target.style.display='none'"
              />
              <span v-if="!authStore.user?.avatar_url" class="text-white text-sm font-medium">{{ userInitials }}</span>
            </div>
            <span class="hidden lg:block text-sm font-medium text-gray-900 dark:text-white truncate max-w-[120px]">
              {{ displayName }}
            </span>
            <ChevronDown class="w-4 h-4 text-gray-500 hidden lg:block" />
          </button>

          <!-- User Dropdown -->
          <div
            v-if="showUserMenu"
            class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2"
          >
            <div class="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
              {{ userRoleLabel }}
            </div>
            <div class="border-t border-gray-200 dark:border-gray-700 my-1" />
            <button
              @click="logout"
              class="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  Menu,
  PanelLeft,
  ChevronRight,
  Sun,
  Moon,
  Bell,
  ChevronDown
} from 'lucide-vue-next'
import { useDarkMode } from '../../composables/useDarkMode'
import { useAuthStore } from '../../../stores/auth'

const props = defineProps<{
  isCollapsed: boolean
}>()

defineEmits<{
  toggleSidebar: []
  openMobileSidebar: []
}>()

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { isDark, toggleDarkMode } = useDarkMode()

// Get user initials for avatar
const userInitials = computed(() => {
  const name = authStore.user?.name || authStore.user?.email || 'Admin'
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
})

// Get display name with truncation
const displayName = computed(() => {
  return authStore.user?.name || authStore.user?.email || 'Admin'
})

// Get user role label in Thai
const userRoleLabel = computed(() => {
  const role = authStore.user?.role
  const roleLabels: Record<string, string> = {
    'admin': 'แอดมิน',
    'owner': 'เจ้าของร้าน',
    'developer': 'นักพัฒนา',
    'photographer': 'ช่างภาพ',
    'makeup_artist': 'ช่างแต่งหน้า',
    'customer': 'ลูกค้า'
  }
  return roleLabels[role] || role || 'ผู้ใช้'
})

const showNotifications = ref(false)
const showUserMenu = ref(false)

interface BreadcrumbItem {
  label: string
  to?: string
}

const pageTitle = computed(() => {
  const titles: Record<string, string> = {
    'admin-dashboard': 'Dashboard',
    'admin-bookings': 'Bookings',
    'admin-booking-detail': 'Booking Details',
    'admin-tasks': 'Tasks',
    'admin-staffs': 'Staffs',
    'admin-packages': 'Packages',
    'admin-settings': 'Settings',
    'admin-executive-summaries': 'Executive Summaries'
  }
  return titles[route.name as string] || 'Dashboard'
})

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = [{ label: 'Admin', to: '/admin' }]
  
  const routeName = route.name as string
  
  switch (routeName) {
    case 'admin-booking-detail':
      items.push({ label: 'Bookings', to: '/admin/bookings' })
      items.push({ label: 'Booking Details' })
      break
    case 'admin-bookings':
      items.push({ label: 'Bookings' })
      break
    case 'admin-tasks':
      items.push({ label: 'Tasks' })
      break
    case 'admin-staffs':
      items.push({ label: 'Staffs' })
      break
    case 'admin-packages':
      items.push({ label: 'Packages' })
      break
    case 'admin-settings':
      items.push({ label: 'Settings' })
      break
    case 'admin-executive-summaries':
      items.push({ label: 'Dashboard', to: '/admin' })
      items.push({ label: 'Executive Summaries' })
      break
    case 'admin-dashboard':
    default:
      items.push({ label: 'Dashboard' })
      break
  }
  
  return items
})

const notifications = [
  { id: 1, message: 'New booking from John Doe', time: '5 minutes ago' },
  { id: 2, message: 'Task "Photo Editing" completed', time: '1 hour ago' },
  { id: 3, message: 'Payment received: $299', time: '2 hours ago' },
]

// Close dropdowns when clicking outside
const closeDropdowns = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('.relative')) {
    showNotifications.value = false
    showUserMenu.value = false
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('click', closeDropdowns)
}

// Logout function
async function logout() {
  await authStore.logout()
  router.push('/admin/login')
}
</script>
